import { API_URL } from '../../../../constants/urls.constants';
import type { GetQueryArgsType } from '../../../../types/global.types';
import { appServiceApi } from '../../../apis/app-service.api';
import { APP_API_TAGS } from '../../../app.constants';
import { invalidateOnSuccess } from '../../../app.utils';
import type {
  AssessmentDetailsResponse,
  EvaluatePayload,
  InterviewDetailsResponse,
  PendingAssessmentsResponse,
  PendingInterviewsResponse,
} from './evaluation.type';

const { ASSESSMENTS, PENDING_INTERVIEWS } = APP_API_TAGS || {};

export const evaluationApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET: Pending Assessment Submissions for Evaluator
    getPendingAssessments: builder.query<
      PendingAssessmentsResponse,
      GetQueryArgsType<{ search?: string; skip?: number; limit?: number }>
    >({
      query: (queryArgs) => ({
        url: API_URL.recruitmentEvaluatorAssessments,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [ASSESSMENTS],
    }),

    // GET: Single Assessment Details for Evaluation
    getAssessmentDetails: builder.query<AssessmentDetailsResponse, string>({
      query: (assignment_id) => ({
        url: `${API_URL.recruitmentEvaluatorAssessments}/${assignment_id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: ASSESSMENTS as any, id }],
    }),

    // POST: Submit Assessment Evaluation
    submitEvaluation: builder.mutation<
      any,
      { assignment_id: string; body: EvaluatePayload }
    >({
      query: ({ assignment_id, body }) => ({
        url: `${API_URL.recruitmentEvaluatorAssessments}/${assignment_id}/evaluate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([
        ASSESSMENTS,
        { type: ASSESSMENTS, id: true },
      ]),
    }),

    // GET: Pending Interview Submissions for Evaluator
    getPendingInterviews: builder.query<
      PendingInterviewsResponse,
      {
        search?: string;
        skip?: number;
        limit?: number;
        interview_status?: string;
        job_id?: string;
        department_id?: string;
      }
    >({
      query: (params) => ({
        url: API_URL.recruitmentEvaluatorInterviews,
        method: 'GET',
        params,
      }),
      providesTags: [PENDING_INTERVIEWS],
    }),

    // GET: Single Interview Details for Evaluation
    getInterviewDetails: builder.query<InterviewDetailsResponse, string>({
      query: (interview_id) => ({
        url: `${API_URL.recruitmentEvaluatorInterviews}/${interview_id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [
        { type: PENDING_INTERVIEWS as any, id },
      ],
    }),

    // POST: Evaluate Interview
    evaluateInterview: builder.mutation<
      any,
      { interview_id: string; body: any }
    >({
      query: ({ interview_id, body }) => ({
        url: `${API_URL.recruitmentEvaluatorInterviews}/${interview_id}/evaluate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([
        PENDING_INTERVIEWS,
        { type: PENDING_INTERVIEWS, id: true },
      ]),
    }),
  }),
});

export const {
  useGetPendingAssessmentsQuery,
  useGetAssessmentDetailsQuery,
  useSubmitEvaluationMutation,
  useGetPendingInterviewsQuery,
  useGetInterviewDetailsQuery,
  useEvaluateInterviewMutation,
} = evaluationApi;
